import { FC } from "react";
import { useSelector } from "react-redux";

import { Automation, Error, Group, Person, Warning } from "@livechat/design-system-icons";
import { DesignToken, Heading, Icon, SpacingToken, Tag, Text } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import PriorityIndicator from "components/priority/PriorityIndicator";
import { TICKET_PRIORITY_LABEL_MAP } from "constants/tickets/priorities";
import { replaceWithSubstring } from "helpers/string";
import { TicketStatusFormattedValues } from "interfaces/entities/ticket";
import { selectAgents } from "store/entities/agents";
import { selectCannedResponses } from "store/entities/canned-responses";
import { IMacro, IMacroAction } from "store/entities/macros/interfaces";
import { selectTags } from "store/entities/tags";
import { selectTeams } from "store/entities/teams";
import { textEllipsisStyles, textMultilineEllipsisStyles } from "styles/common";
import { withVar } from "themes/helpers";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const MacroPreviewActionItem = (action: IMacroAction) => {
  const teams = useSelector(selectTeams);
  const agents = useSelector(selectAgents);
  const cannedResponses = useSelector(selectCannedResponses);
  const tags = useSelector(selectTags);

  if (action.type === "assign") {
    const teamRemoved = !teams[action.teamID as string];
    const agentRemoved = !agents[action.agentID as string] && !action.randomAgent && action.agentID !== null;

    return (
      <Flex column gap={withVar(SpacingToken.Spacing2)}>
        <Block marginTop={withVar(SpacingToken.Spacing05)}>
          <Text size="sm" bold>
            Set ticket assignment:
          </Text>
        </Block>
        <Flex flexWrap maxWidth="270px" gap={withVar(SpacingToken.Spacing1)}>
          <Tag leftNode={<Icon source={teamRemoved ? Warning : Group} />} kind={teamRemoved ? "error" : "default"}>
            {teamRemoved ? "Team removed" : teams[action.teamID as string]?.name}
          </Tag>
          <Tag leftNode={<Icon source={agentRemoved ? Warning : Person} />} kind={agentRemoved ? "error" : "default"}>
            {action.randomAgent && "Random agent"}
            {action.agentID === null && "Unassigned"}
            {agentRemoved ? "Agent removed" : agents[action.agentID as string]?.name}
          </Tag>
        </Flex>
      </Flex>
    );
  }

  if (action.type === "sendRatingRequest" || action.type === "sendFollowUp") {
    const isRatingRequest = action.type === "sendRatingRequest";
    const isTemplateAction = action.template;
    const isCannedAction = action.cannedResponseID;
    const cannedRemoved = action.cannedResponseID && !cannedResponses[action.cannedResponseID];

    return (
      <Flex column gap={withVar(SpacingToken.Spacing1)}>
        <Block marginTop={withVar(SpacingToken.Spacing05)}>
          <Text size="sm" bold>
            {isRatingRequest ? "Send feedback request:" : "Send follow-up:"}
          </Text>
        </Block>
        <Text size="sm" css={textMultilineEllipsisStyles(3)}>
          {isCannedAction && (
            <>
              {cannedRemoved ? (
                <Tag leftNode={<Icon source={Error} />} kind="error">
                  Canned response removed
                </Tag>
              ) : (
                replaceWithSubstring(cannedResponses[action.cannedResponseID as string]?.text, "[cid", "]", "[Image]")
              )}
            </>
          )}
          {isTemplateAction && action.template?.text}
        </Text>
      </Flex>
    );
  }

  if (action.type === "message") {
    const isPrivateAction = action.isPrivate;
    const isCannedAction = action.cannedResponseID;
    const isTemplateAction = action.template;
    const cannedRemoved = action.cannedResponseID && !cannedResponses[action.cannedResponseID];

    return (
      <Flex column gap={withVar(SpacingToken.Spacing1)}>
        <Block marginTop={withVar(SpacingToken.Spacing05)}>
          <Text size="sm" bold>
            {isPrivateAction ? "Send private note:" : "Send message:"}
          </Text>
        </Block>
        <Text size="sm" css={textMultilineEllipsisStyles(3)}>
          {isCannedAction && (
            <>
              {cannedRemoved ? (
                <Tag leftNode={<Icon source={Error} />} kind="error">
                  Canned response removed
                </Tag>
              ) : (
                replaceWithSubstring(cannedResponses[action.cannedResponseID as string]?.text, "[cid", "]", "[Image]")
              )}
            </>
          )}
          {isTemplateAction && action.template?.text}
        </Text>
      </Flex>
    );
  }

  if (action.type === "status") {
    return (
      <Flex gap={withVar(SpacingToken.Spacing2)}>
        <Text size="sm" bold>
          Set status:
        </Text>
        <Text size="sm">{TicketStatusFormattedValues[action.status]}</Text>
      </Flex>
    );
  }

  if (action.type === "priority") {
    return (
      <Flex>
        <Text size="sm" bold>
          Set priority:
        </Text>
        <Block
          marginV={withVar(SpacingToken.Spacing05)}
          marginLeft={withVar(SpacingToken.Spacing2)}
          marginRight={withVar(SpacingToken.Spacing1)}
        >
          <PriorityIndicator priority={action.priority} />
        </Block>
        <Text size="sm">{TICKET_PRIORITY_LABEL_MAP[action.priority]}</Text>
      </Flex>
    );
  }

  if (action.type === "addTags" || action.type === "removeTags") {
    const isAddTagsAction = action.type === "addTags";

    return (
      <Flex alignItems="center" gap={withVar(SpacingToken.Spacing1)} flexWrap maxWidth="270px">
        <Text size="sm" bold>
          {isAddTagsAction ? "Add tags:" : "Remove tags:"}
        </Text>
        {action.tagIDs.map(tagID => {
          const tagRemoved = !tags[tagID];

          return (
            <Tag
              key={tagID}
              kind={tagRemoved ? "error" : "warning"}
              leftNode={tagRemoved ? <Icon source={Error} /> : undefined}
            >
              {tagRemoved ? "Tag removed" : tags[tagID]?.name}
            </Tag>
          );
        })}
      </Flex>
    );
  }

  if (action.type === "spam") {
    const moveToSpam = action.spam;

    return (
      <Flex alignItems="center">
        <Text size="sm" bold>
          {moveToSpam ? "Move to spam" : "Move to all recent tickets"}
        </Text>
      </Flex>
    );
  }

  if (action.type === "addFollowers" || action.type === "removeFollowers") {
    const isAddFollowersAction = action.type === "addFollowers";

    return (
      <Flex alignItems="center" gap={withVar(SpacingToken.Spacing1)} flexWrap maxWidth="270px">
        <Text size="sm" bold>
          {isAddFollowersAction ? "Add followers:" : "Remove followers:"}
        </Text>
        {action.followers.map(agentID => {
          const agentRemoved = !agents[agentID];

          return (
            <Tag
              key={agentID}
              kind={agentRemoved ? "error" : "default"}
              leftNode={agentRemoved ? <Icon source={Error} /> : undefined}
            >
              {agentRemoved ? "Agent removed" : agents[agentID]?.name}
            </Tag>
          );
        })}
      </Flex>
    );
  }

  return null;
};

interface IMacroPreviewItemProps {
  action: IMacroAction;
  hideConnection?: boolean;
}

const MacroPreviewItem: FC<IMacroPreviewItemProps> = ({ action, hideConnection }) => (
  <Flex width="100%">
    <Flex width={withVar(SpacingToken.Spacing6)} column>
      <Flex alignItems="center" justifyContent="flex-start" width="100%" minHeight={withVar(SpacingToken.Spacing6)}>
        <Icon source={Automation} size="xsmall" customColor={withVar(DesignToken.ActionPrimaryDefault)} />
      </Flex>
      <Block
        marginH="5px"
        width="14px"
        height="100%"
        css={{
          borderLeft: `${withVar(SpacingToken.Spacing05)} solid ${withVar(
            hideConnection ? DesignToken.TooltipBackgroundBasic : DesignToken.SurfaceAccentEmphasisSubtleInfo
          )}`,
        }}
      />
    </Flex>
    <Flex
      marginBottom={!hideConnection ? withVar(SpacingToken.Spacing5) : undefined}
      minHeight={withVar(SpacingToken.Spacing6)}
      alignItems="center"
    >
      <MacroPreviewActionItem {...action} />
    </Flex>
  </Flex>
);

interface IProps {
  macro: IMacro;
}

export const MacroPreview: FC<IProps> = ({ macro }) => {
  const { name, actions } = macro;

  return (
    <Flex width="296px" maxHeight="320px" overflow="auto" column>
      <Block marginBottom={withVar(SpacingToken.Spacing2)}>
        <Heading size="sm" css={textEllipsisStyles}>
          {name}
        </Heading>
      </Block>
      {actions.map((action, index) => (
        <MacroPreviewItem
          // eslint-disable-next-line react/no-array-index-key
          key={`${action.type}${index}`}
          action={action}
          hideConnection={index === actions.length - 1}
        />
      ))}
    </Flex>
  );
};
